import { AxiosError, AxiosPromise, AxiosResponse } from 'axios';
import {Action, AnyAction, MiddlewareAPI} from 'redux';
import { ThunkMiddleware } from 'redux-thunk';

import { LoggingContext } from '~src/services/logger/LlsLogger';
import { notificationsActionsSwitcher } from '~src/store/app/notificationsActionsSwitcher';
import {ErrResp} from "~src/shared/errors/interfaces";

export interface ActionTypes {
  Request: string;
  Success: string;
  Failed: string;
}

export interface AsyncAction<RESPONSE, LOCALPAYLOAD> {
  types: [string, string, string] | ActionTypes;
  payload?: Omit<LOCALPAYLOAD, 'type'>;
  callAPI: () => AxiosPromise<RESPONSE>;
}

export type ApiReduxResp<R, E = ErrResp> = Promise<ThunkAnyAction<R, E>>;

export interface ThunkSuccessAction<R> extends Action {
  response: AxiosResponse<R>;
}

export interface ThunkFailAction<E> extends AnyAction {
  error: AxiosError<E>;
}

export function getLoggingContext(failAction: ThunkFailAction<unknown>, componentStack?: string): LoggingContext {
  return {
    error: new Error(failAction.error.message),
    info: {
      componentStack: componentStack,
    },
  };
}

export type ThunkAnyAction<R, E> = ThunkSuccessAction<R> | ThunkFailAction<E>;
// TODO FIXME: this interface does't meet expectations from react-redux connect.
export type ThunkMWDispatch = <RESPONSE, LOCALPAYLOAD, ERESPONSE, ANYACTION = AnyAction>(
  action: ANYACTION,
) => ANYACTION extends AsyncAction<RESPONSE, LOCALPAYLOAD> ? Promise<ThunkAnyAction<RESPONSE, ERESPONSE>> : ANYACTION;

export function isSuccess<R>(action: ThunkAnyAction<R, unknown>): action is ThunkSuccessAction<R> {
  return Boolean(action.response && !(action as ThunkFailAction<never>).error)
}
export function isError<E>(action: ThunkAnyAction<unknown, E>): action is ThunkFailAction<E> {
  const asFail = action as ThunkFailAction<never>;
  return Boolean(asFail.error && asFail.error.isAxiosError);
}

const callAPIMiddleware: ThunkMiddleware =
  <RESPONSE, LOCALPAYLOAD, ERESPONSE>({ dispatch }: MiddlewareAPI) =>
    next =>
      (action: AsyncAction<RESPONSE, LOCALPAYLOAD> | AnyAction): Promise<ThunkAnyAction<RESPONSE, ERESPONSE>> | AnyAction => {
    if (!action) {
      throw new Error('No correct action');
    }

    if ((action as AnyAction).type) {
      // Normal action: pass it on
      return next(action as AnyAction);
    }

    const { types, callAPI, payload = {} } = action as AsyncAction<RESPONSE, LOCALPAYLOAD>;
    let requestType: string;
    let successType: string;
    let failureType: string;
    if (Array.isArray(types)) {
      [requestType, successType, failureType] = types;
    } else {
      requestType = types.Request;
      successType = types.Success;
      failureType = types.Failed;
    }

    dispatch({
      ...payload,
      type: requestType,
    });

    return callAPI().then(
      (response): ThunkSuccessAction<RESPONSE> => {
        const successAction: ThunkSuccessAction<RESPONSE> = {
          ...payload,
          response,
          type: successType,
        };
        dispatch<ThunkSuccessAction<RESPONSE>>(successAction);
        const notifAction = notificationsActionsSwitcher(successAction);
        if (notifAction) {
          dispatch(notifAction);
        }

        return successAction;
      },
      (error: AxiosError): ThunkFailAction<ERESPONSE> =>
        dispatch<ThunkFailAction<ERESPONSE>>({
          ...payload,
          error,
          type: failureType,
        }),
    );
  };

export default callAPIMiddleware;
